/**
 * Quick Add token parser.
 *
 * Runs entirely in the browser: no AI, no network, no latency. Capture has to
 * be instant or it does not happen, and a round trip per keystroke would make
 * the chips lag behind the text.
 *
 * Everything it recognizes is shown back as a removable chip, so the parse is
 * always visible and always correctable. Silent magic that occasionally
 * guesses wrong is worse than no magic at all.
 */

import * as chrono from 'chrono-node';

import type { Energy, Priority } from './types';

export interface ParsedToken {
  type: 'estimate' | 'priority' | 'client' | 'project' | 'energy' | 'due' | 'schedule';
  /** Exact source text, so it can be removed from the input verbatim. */
  raw: string;
  /** Human-readable chip label. */
  label: string;
}

export interface ParseResult {
  title: string;
  estimate_minutes: number | null;
  priority: Priority | null;
  energy: Energy | null;
  client_id: number | null;
  project_id: number | null;
  due_at: Date | null;
  /**
   * Whether the parsed date carried a clock time.
   *
   * "friday" is a deadline; "friday at 2pm" is an appointment. Callers that can
   * act on the difference (Plan schedules one and files the other) need to know
   * which was said, and chrono is the only place that still knows.
   */
  due_has_time: boolean;
  /**
   * `>fri 9am`: when to put it on the planner, said explicitly.
   *
   * A due date says when work must be finished; this says when it will be
   * done. They are different decisions and often different days, so the token
   * is separate rather than overloading the date already in the line.
   */
  schedule_at: Date | null;
  /** False for `>fri`: a day was chosen but not an hour. */
  schedule_has_time: boolean;
  tokens: ParsedToken[];
}

export interface NamedEntity {
  id: number;
  name: string;
}

/** `~45m`, `~1.5h`, `~90` (bare number means minutes). */
const ESTIMATE = /(?:^|\s)~(\d+(?:\.\d+)?)(h|hr|hrs|m|min|mins)?\b/i;

/** `!1` urgent .. `!4` low. Digits, not words: fewer keystrokes and no ambiguity. */
const PRIORITY = /(?:^|\s)!([1-4])\b/;

const ENERGY = /(?:^|\s)\/(high|med|medium|low)\b/i;

/** `>` opens a scheduling phrase: `>tomorrow 2pm`, `>fri`, `>2pm`. */
const SCHEDULE_SIGIL = /(?:^|\s)>\s*(?=\S)/;

const PRIORITY_BY_DIGIT: Record<string, Priority> = {
  '1': 'urgent',
  '2': 'high',
  '3': 'normal',
  '4': 'low',
};

const ENERGY_ALIASES: Record<string, Energy> = {
  high: 'high',
  med: 'medium',
  medium: 'medium',
  low: 'low',
};

function toMinutes(amount: number, unit: string | undefined): number {
  const isHours = unit !== undefined && unit.toLowerCase().startsWith('h');
  return Math.round(isHours ? amount * 60 : amount);
}

/**
 * Match `@acme` or `#redesign` against a list by name.
 *
 * Prefers an exact match, then a prefix match, then a substring match, so
 * typing `@ac` finds Acme without `@acme` ever resolving to something else
 * once both exist. Ambiguous prefixes deliberately resolve to nothing rather
 * than to an arbitrary winner.
 */
export function matchEntity(query: string, entities: NamedEntity[]): NamedEntity | null {
  const needle = query.toLowerCase().replace(/[-_]/g, ' ');
  if (!needle) return null;

  const normalized = entities.map((e) => ({
    entity: e,
    name: e.name.toLowerCase(),
  }));

  const exact = normalized.filter((e) => e.name === needle);
  if (exact.length === 1) return exact[0]!.entity;

  const prefix = normalized.filter((e) => e.name.startsWith(needle));
  if (prefix.length === 1) return prefix[0]!.entity;

  const substring = normalized.filter((e) => e.name.includes(needle));
  if (substring.length === 1) return substring[0]!.entity;

  return null;
}

function extractNamed(
  text: string,
  sigil: '@' | '#',
  entities: NamedEntity[],
): { text: string; match: NamedEntity | null; raw: string | null } {
  // Names can contain spaces, so allow a quoted form: @"Acme Corp".
  const pattern = new RegExp(`(?:^|\\s)${sigil}(?:"([^"]+)"|([\\w-]+))`, 'i');
  const found = pattern.exec(text);
  if (!found) return { text, match: null, raw: null };

  const query = found[1] ?? found[2] ?? '';
  const match = matchEntity(query, entities);

  // An unresolved sigil is left in the title untouched. Stripping text that
  // matched nothing would silently eat part of what the user wrote.
  if (!match) return { text, match: null, raw: null };

  return {
    text: text.replace(found[0], ' '),
    match,
    raw: found[0].trim(),
  };
}

/**
 * Pull a `>` scheduling phrase out of the text.
 *
 * chrono runs on the remainder after the sigil and must match at its very
 * start, so `>fri call Dave` takes only "fri". A `>` that leads to nothing
 * chrono understands is left in the title rather than silently eaten.
 */
function extractSchedule(
  text: string,
  now: Date,
): { text: string; at: Date | null; hasTime: boolean; raw: string | null } {
  const sigil = SCHEDULE_SIGIL.exec(text);
  if (!sigil) return { text, at: null, hasTime: false, raw: null };

  const phraseStart = sigil.index + sigil[0].length;
  const found = chrono.parse(text.slice(phraseStart), now, { forwardDate: true })[0];
  if (!found || found.index !== 0 || found.text.trim().length < 2) {
    return { text, at: null, hasTime: false, raw: null };
  }

  const rawStart = sigil[0].startsWith(' ') ? sigil.index + 1 : sigil.index;
  const rawEnd = phraseStart + found.text.length;

  return {
    text: `${text.slice(0, rawStart)} ${text.slice(rawEnd)}`,
    at: found.date(),
    hasTime: found.start.isCertain('hour'),
    raw: text.slice(rawStart, rawEnd).trim(),
  };
}

export function parseQuickAdd(
  input: string,
  options: { clients?: NamedEntity[]; projects?: NamedEntity[]; now?: Date } = {},
): ParseResult {
  const { clients = [], projects = [], now = new Date() } = options;

  let text = input;
  const tokens: ParsedToken[] = [];

  let estimate: number | null = null;
  const estimateMatch = ESTIMATE.exec(text);
  if (estimateMatch) {
    estimate = toMinutes(Number(estimateMatch[1]), estimateMatch[2]);
    if (estimate > 0) {
      text = text.replace(estimateMatch[0], ' ');
      tokens.push({
        type: 'estimate',
        raw: estimateMatch[0].trim(),
        label: estimate >= 60 ? `${(estimate / 60).toFixed(1).replace(/\.0$/, '')}h` : `${estimate}m`,
      });
    } else {
      estimate = null;
    }
  }

  let priority: Priority | null = null;
  const priorityMatch = PRIORITY.exec(text);
  if (priorityMatch) {
    priority = PRIORITY_BY_DIGIT[priorityMatch[1]!] ?? null;
    if (priority) {
      text = text.replace(priorityMatch[0], ' ');
      tokens.push({ type: 'priority', raw: priorityMatch[0].trim(), label: priority });
    }
  }

  let energy: Energy | null = null;
  const energyMatch = ENERGY.exec(text);
  if (energyMatch) {
    energy = ENERGY_ALIASES[energyMatch[1]!.toLowerCase()] ?? null;
    if (energy) {
      text = text.replace(energyMatch[0], ' ');
      tokens.push({ type: 'energy', raw: energyMatch[0].trim(), label: `${energy} focus` });
    }
  }

  const clientResult = extractNamed(text, '@', clients);
  text = clientResult.text;
  if (clientResult.match) {
    tokens.push({
      type: 'client',
      raw: clientResult.raw!,
      label: clientResult.match.name,
    });
  }

  const projectResult = extractNamed(text, '#', projects);
  text = projectResult.text;
  if (projectResult.match) {
    tokens.push({
      type: 'project',
      raw: projectResult.raw!,
      label: projectResult.match.name,
    });
  }

  // Scheduling before the due parse, so chrono cannot read `>fri 9am` as the
  // deadline it is not.
  const schedule = extractSchedule(text, now);
  text = schedule.text;
  if (schedule.at) {
    tokens.push({
      type: 'schedule',
      raw: schedule.raw!,
      label: `Plan ${formatDue(schedule.at, now)}`,
    });
  }

  // Dates last, on what remains. Running chrono first would let it swallow
  // numbers that belong to an estimate ("~45m" reads as a time to chrono).
  let dueAt: Date | null = null;
  let dueHasTime = false;
  const dateResults = chrono.parse(text, now, { forwardDate: true });
  const dateMatch = dateResults[0];
  if (dateMatch && dateMatch.text.trim().length > 1) {
    dueAt = dateMatch.date();
    dueHasTime = dateMatch.start.isCertain('hour');
    text = text.replace(dateMatch.text, ' ');
    tokens.push({ type: 'due', raw: dateMatch.text.trim(), label: formatDue(dueAt, now) });
  }

  return {
    title: text.replace(/\s+/g, ' ').trim(),
    estimate_minutes: estimate,
    priority,
    energy,
    client_id: clientResult.match?.id ?? null,
    project_id: projectResult.match?.id ?? null,
    due_at: dueAt,
    due_has_time: dueHasTime,
    schedule_at: schedule.at,
    schedule_has_time: schedule.hasTime,
    tokens,
  };
}

/** Chip label for a due date: relative when near, absolute when not. */
export function formatDue(date: Date, now: Date): string {
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round(
    (startOfDay(date).getTime() - startOfDay(now).getTime()) / 86_400_000,
  );

  const time = date.getHours() || date.getMinutes()
    ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : '';

  if (days === 0) return time ? `Today ${time}` : 'Today';
  if (days === 1) return time ? `Tomorrow ${time}` : 'Tomorrow';
  if (days > 1 && days < 7) {
    const weekday = date.toLocaleDateString(undefined, { weekday: 'long' });
    return time ? `${weekday} ${time}` : weekday;
  }

  const absolute = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return time ? `${absolute} ${time}` : absolute;
}
